import { useSyncExternalStore } from 'react'
import { isApiUnavailable } from './errors'

/**
 * App-wide "is the API reachable?" flag, fed by the QueryCache /
 * MutationCache callbacks in ``QueryProvider``. A query only reports its
 * failure after the retries ran out, so a blip doesn't flip it.
 */
export interface ApiHealth {
  down: boolean
  /** ``Date.now()`` of the failure that marked it down. */
  since: number | null
}

const UP: ApiHealth = { down: false, since: null }

let state: ApiHealth = UP
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

export function getApiHealth(): ApiHealth {
  return state
}

export function subscribeApiHealth(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Marks the API down when ``err`` means the server couldn't be reached. */
export function reportApiFailure(err: unknown): void {
  if (state.down || !isApiUnavailable(err)) return
  state = { down: true, since: Date.now() }
  emit()
}

/** Any successful request clears the flag. */
export function reportApiSuccess(): void {
  if (!state.down) return
  state = UP
  emit()
}

export function useApiHealth(): ApiHealth {
  return useSyncExternalStore(subscribeApiHealth, getApiHealth, getApiHealth)
}
